import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Checks if a given username is already taken by another user.
 * Queries the Firestore 'users' collection for uniqueness.
 * 
 * @param username The username to check for uniqueness.
 * @returns {Promise<boolean>} True if the username is unique and available, False if it is taken.
 */
export const checkUsernameUnique = async (username: string, excludeUid?: string): Promise<boolean> => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('username', '==', username));
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) return true;
        
        if (excludeUid && snapshot.size === 1 && snapshot.docs[0].id === excludeUid) {
            return true;
        }
        
        return false;
    } catch (error) {
        console.error("Error verifying username uniqueness against Firestore:", error);
        return false;
    }
};

/**
 * Retrieves the UID for a given username.
 * 
 * @param username The username to look up.
 * @returns {Promise<string | null>} The UID if found, null otherwise.
 */
export const getUidByUsername = async (username: string): Promise<string | null> => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('username', '==', username));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
            return snapshot.docs[0].id;
        }
        return null;
    } catch (error) {
        console.error(`Error fetching UID for username ${username}:`, error);
        return null;
    }
};

/**
 * Retrieves the UID, photoURL, displayName, and igns for a given username or playerId.
 * Used by squad invite search to display the found player's info.
 * 
 * @param username The username or playerId to look up.
 * @returns {Promise<{ uid: string; photoURL: string; displayName: string; igns: any[] } | null>} The user info if found, null otherwise.
 */
export const getUserByUsername = async (username: string): Promise<{ uid: string; photoURL: string; displayName: string; igns: any[] } | null> => {
    try {
        const usersRef = collection(db, 'users');
        // If input is a 10-digit number, search by playerId instead
        const isPlayerId = /^\d{10}$/.test(username.trim());
        const q = query(usersRef, where(isPlayerId ? 'playerId' : 'username', '==', username.trim()));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
            const docData = snapshot.docs[0].data();
            return {
                uid: snapshot.docs[0].id,
                photoURL: docData.photoURL || docData.avatar || '',
                displayName: docData.username || 'Player',
                igns: docData.igns || []
            };
        }
        return null;
    } catch (error) {
        console.error(`Error fetching user for ${username}:`, error);
        return null;
    }
};

/**
 * Checks if a given UID is already in a squad for a specific game.
 * 
 * @param uid The UID to check.
 * @param game The game to check against.
 * @returns {Promise<boolean>} True if the user is ALREADY IN A SQUAD for that game.
 */
export const checkUserInSquad = async (uid: string, game: string): Promise<boolean> => {
    try {
        const squadsRef = collection(db, 'squads');
        const q = query(squadsRef, where('game', '==', game), where('memberUids', 'array-contains', uid));
        const snapshot = await getDocs(q);
        
        return !snapshot.empty;
    } catch (error) {
        console.error("Error verifying global squad limits against Firestore:", error);
        return true; // Fail safe to true (block creation) on DB errors
    }
};
